import React, { useContext, useMemo, useState, MouseEvent } from 'react';
import { useParams } from 'react-router-dom';
import {
  Box,
  Card,
  CardActions,
  CardContent,
  Checkbox,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Menu,
  MenuItem,
  Pagination,
  Paper,
  Select,
  SelectChangeEvent,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  Avatar,
  Button as MuiButton,
  SxProps,
  Theme,
} from '@mui/material';
import {
  Users,
  Search,
  ChevronDown,
  LayoutGrid,
  List,
  Briefcase,
  Sparkles,
  ExternalLink,
  X,
  Filter,
} from 'lucide-react';
import { toast } from '../../../../ui/toast';
import { MoveDropdown } from '../../../../ui/MoveDropdown/MoveDropdown';
import { editJobApplicant } from '../../../../utils/api_service/JobOpenings';
import { Applicant, TabData } from '../../../../utils/types/JobOpenings';
import { JobOpeningsContext } from '../../../../utils/contexts/JobOpening';

export interface ScatterModalProps {
  segmentedCandidates: Applicant[];
  selectedCandidates: TabData[];
  setSelectedCandidates: (tabDataObject: TabData[]) => void | undefined;
  setTabTitles: (titles: string[]) => void | undefined;
  setActiveTab: (value: number) => void;
  tabTitles: string[];
  useSkillPercentage?: boolean;
  onCandidateClick?: (
    candidate: Applicant,
    tileCandidates?: Applicant[],
    tileName?: string
  ) => void;
  currentTab?: string;
  isApplicantSelected?: (applicant: Applicant) => boolean;
  filteredSkills?: string[];
}

export const getStatusChipConfig = (
  status?: string
): { label: string; sx: SxProps<Theme> } => {
  switch (status) {
    case 'Outstanding':
      return {
        label: 'Outstanding',
        sx: {
          bgcolor: 'rgba(147, 51, 234, 0.08)',
          color: '#7e22ce',
          border: '1px solid rgba(147, 51, 234, 0.25)',
          fontWeight: 600,
        },
      };
    case 'Auto-Assessment':
      return {
        label: 'Auto-Assessment',
        sx: {
          bgcolor: 'rgba(217, 119, 6, 0.08)',
          color: '#b45309',
          border: '1px solid rgba(217, 119, 6, 0.25)',
          fontWeight: 600,
        },
      };
    case 'Shortlisted':
      return {
        label: 'Shortlisted',
        sx: {
          bgcolor: 'rgba(5, 150, 105, 0.08)',
          color: '#047857',
          border: '1px solid rgba(5, 150, 105, 0.25)',
          fontWeight: 600,
        },
      };
    case 'Suitable':
      return {
        label: 'Suitable',
        sx: {
          bgcolor: 'rgba(37, 99, 235, 0.08)',
          color: '#1d4ed8',
          border: '1px solid rgba(37, 99, 235, 0.25)',
          fontWeight: 600,
        },
      };
    case 'New':
      return {
        label: 'New',
        sx: {
          bgcolor: 'rgba(2, 132, 199, 0.08)',
          color: '#0369a1',
          border: '1px solid rgba(2, 132, 199, 0.25)',
          fontWeight: 600,
        },
      };
    case 'Unsuccessful-Closed':
    case 'Unsuccessful':
      return {
        label: 'Unsuccessful',
        sx: {
          bgcolor: 'rgba(225, 29, 72, 0.08)',
          color: '#be123c',
          border: '1px solid rgba(225, 29, 72, 0.25)',
          fontWeight: 600,
        },
      };
    case 'Unsuccessful-Pending':
      return {
        label: 'Pending Unsuccessful',
        sx: {
          bgcolor: 'rgba(234, 88, 12, 0.08)',
          color: '#c2410c',
          border: '1px solid rgba(234, 88, 12, 0.25)',
          fontWeight: 600,
        },
      };
    default:
      return {
        label: status || 'New',
        sx: {
          bgcolor: 'rgba(100, 116, 139, 0.08)',
          color: '#475569',
          border: '1px solid rgba(100, 116, 139, 0.2)',
          fontWeight: 600,
        },
      };
  }
};

export const ScatterModal = ({
  segmentedCandidates,
  selectedCandidates,
  setSelectedCandidates,
  setTabTitles,
  setActiveTab,
  tabTitles,
  useSkillPercentage,
  onCandidateClick,
  currentTab,
  isApplicantSelected,
  filteredSkills,
}: ScatterModalProps) => {
  const { id } = useParams<{ id: string }>();
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [pageSize, setPageSize] = useState<number>(12);
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('grid');
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [statusFilter, setStatusFilter] = useState<string>('ALL');
  const [openDropdown, setOpenDropdown] = useState<string | null>(null);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);

  // Menu anchor states
  const [selectMenuAnchor, setSelectMenuAnchor] = useState<null | HTMLElement>(null);
  const [bulkMenuAnchor, setBulkMenuAnchor] = useState<null | HTMLElement>(null);

  const { jobOpeningElements } = useContext(JobOpeningsContext);

  const options = [
    'Outstanding',
    'Auto-Assessment',
    'Shortlisted',
    'Unsuccessful',
    'Suitable',
    'New',
    ...(jobOpeningElements?.includes('Psychometric') ? ['Psychometric'] : []),
    ...(jobOpeningElements?.includes('Wellness') ? ['Wellness'] : []),
  ];

  // Map display values to API values
  const mapStatusToAPI = (displayStatus: string): string => {
    const statusMap: Record<string, string> = {
      Outstanding: 'Outstanding',
      'Auto-Assessment': 'Auto-Assessment',
      Shortlisted: 'Shortlisted',
      Unsuccessful: 'Unsuccessful-Pending',
      Suitable: 'Suitable',
      New: 'New',
    };
    return statusMap[displayStatus] || displayStatus;
  };

  // Helper to get initials
  const getInitials = (name: string) => {
    const parts = name.trim().split(' ').filter(Boolean);
    if (parts.length > 1) {
      return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
    }
    return parts[0] ? parts[0].slice(0, 2).toUpperCase() : 'NA';
  };

  // Status breakdown counts for quick filter tabs
  const statusCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    segmentedCandidates.forEach((c) => {
      const status = c.applicantStatus || 'New';
      counts[status] = (counts[status] || 0) + 1;
    });
    return counts;
  }, [segmentedCandidates]);

  // Filter candidates based on search term & status
  const filteredCandidates = useMemo(() => {
    return segmentedCandidates.filter((candidate) => {
      if (statusFilter !== 'ALL') {
        const candidateStatus = candidate.applicantStatus || 'New';
        if (statusFilter === 'Unsuccessful') {
          if (!candidateStatus.toLowerCase().includes('unsuccessful')) {
            return false;
          }
        } else if (candidateStatus !== statusFilter) {
          return false;
        }
      }

      if (!searchTerm.trim()) return true;
      const query = searchTerm.toLowerCase();

      const nameMatch = candidate.name?.toLowerCase().includes(query) || false;
      const emailMatch = candidate.email?.toLowerCase().includes(query) || false;
      const skillsMatch = candidate.matchedSkills?.some((skill) =>
        skill.toLowerCase().includes(query)
      ) || false;
      const keywordMatch = candidate.keywords?.some((kw) =>
        kw.toLowerCase().includes(query)
      ) || false;
      const behaviorMatch = candidate.matchedBehavior?.some((b) =>
        b.toLowerCase().includes(query)
      ) || false;

      return nameMatch || emailMatch || skillsMatch || keywordMatch || behaviorMatch;
    });
  }, [segmentedCandidates, statusFilter, searchTerm]);

  // Pagination calculation
  const effectivePageSize = pageSize === 0 ? filteredCandidates.length || 1 : pageSize;
  const totalPages = Math.max(1, Math.ceil(filteredCandidates.length / effectivePageSize));

  const indexOfLastItem = currentPage * effectivePageSize;
  const indexOfFirstItem = indexOfLastItem - effectivePageSize;
  const currentItems = useMemo(() => {
    if (pageSize === 0) return filteredCandidates;
    return filteredCandidates.slice(indexOfFirstItem, indexOfLastItem);
  }, [filteredCandidates, indexOfFirstItem, indexOfLastItem, pageSize]);

  const openTab = (name: string, userId: string) => {
    const candidate = segmentedCandidates.find((item) => item.userId === userId);

    if (onCandidateClick && candidate) {
      onCandidateClick(candidate);
      return;
    }

    const nameExists = selectedCandidates.some(
      (item) => item.candidate && item.candidate.userId === userId
    );
    if (!nameExists) {
      setTabTitles([...tabTitles, name]);
      const selectedItem: Applicant | undefined = segmentedCandidates.find(
        (item) => item.userId === userId
      );
      const newSelectedCandidates = [
        ...selectedCandidates,
        { tabId: tabTitles.length, candidate: selectedItem },
      ];
      setSelectedCandidates(newSelectedCandidates);
      setActiveTab(tabTitles.length);
    } else {
      toast.error('Tab for ' + name + ' already open', {
        position: 'bottom-center',
      });
    }
  };

  const handleDropdownToggle = (candidateId: string) => {
    setOpenDropdown((prev) => (prev === candidateId ? null : candidateId));
  };

  const handleOptionClick = async (
    candidateId: string | string[],
    option: string
  ) => {
    const ids = Array.isArray(candidateId) ? candidateId : [candidateId];

    if (!id) {
      toast.error('Job opening ID not found', {
        position: 'bottom-center',
      });
      return;
    }

    try {
      await Promise.all(
        ids.map((applicantId) =>
          editJobApplicant(id, applicantId, {
            applicantStatus: mapStatusToAPI(option),
          })
        )
      );

      toast.success(
        `Successfully moved ${ids.length} candidate(s) to ${
          option === 'Unsuccessful' ? 'Unsuccessful (Pending)' : option
        }`,
        {
          position: 'bottom-center',
        }
      );
      setSelectedItems([]);
    } catch (error) {
      toast.error('Error changing applicant status', {
        position: 'bottom-center',
      });
    }
    setOpenDropdown(null);
    setBulkMenuAnchor(null);
  };

  const handleSelectAll = (type: 'all' | 'page' | 'clear') => {
    if (type === 'clear') {
      setSelectedItems([]);
    } else if (type === 'all') {
      const allIds = filteredCandidates.map((item) => item.userId);
      if (selectedItems.length === allIds.length) {
        setSelectedItems([]);
      } else {
        setSelectedItems(allIds);
      }
    } else {
      const currentPageIds = currentItems.map((item) => item.userId);
      const isCurrentPageFullySelected = currentPageIds.every((item) =>
        selectedItems.includes(item)
      );
      if (isCurrentPageFullySelected) {
        setSelectedItems((prev) => prev.filter((id) => !currentPageIds.includes(id)));
      } else {
        const newSelection = new Set([...selectedItems, ...currentPageIds]);
        setSelectedItems(Array.from(newSelection));
      }
    }
    setSelectMenuAnchor(null);
  };

  const toggleSelectItem = (userId: string) => {
    setSelectedItems((prev) =>
      prev.includes(userId) ? prev.filter((id) => id !== userId) : [...prev, userId]
    );
  };

  const isAllCurrentSelected =
    currentItems.length > 0 &&
    currentItems.every((item) => selectedItems.includes(item.userId));

  // Render avatar using MUI Avatar
  const renderAvatar = (item: Applicant) => {
    const isSelected = isApplicantSelected && isApplicantSelected(item);
    const applicantStatus = item.applicantStatus || '';
    const initials = getInitials(item.name || `${item.firstName} ${item.lastName}`);

    let bgcolor = '#2563eb';
    if (applicantStatus === 'Unsuccessful-Closed') {
      bgcolor = '#e11d48';
    } else if (applicantStatus === 'Unsuccessful-Pending') {
      bgcolor = isSelected ? '#f59e0b' : '#ea580c';
    } else if (applicantStatus === 'Outstanding') {
      bgcolor = '#7c3aed';
    } else if (applicantStatus === 'Shortlisted') {
      bgcolor = '#059669';
    }

    if (item.symbol && item.symbol !== 'circle') {
      return (
        <Avatar
          src={item.symbol.replace('image://', '')}
          alt={item.name}
          sx={{
            width: 38,
            height: 38,
            fontSize: '13px',
            fontWeight: 700,
            border: '2px solid #f1f5f9',
          }}
        >
          {initials}
        </Avatar>
      );
    }

    return (
      <Avatar
        sx={{
          width: 38,
          height: 38,
          fontSize: '13px',
          fontWeight: 700,
          bgcolor,
          color: '#ffffff',
          boxShadow: '0 2px 4px rgba(0,0,0,0.08)',
        }}
      >
        {initials}
      </Avatar>
    );
  };

  // Render status chip
  const renderStatusChip = (status?: string) => {
    const config = getStatusChipConfig(status);
    return (
      <Chip
        label={config.label}
        size="small"
        sx={{
          fontSize: '11px',
          height: '22px',
          letterSpacing: '-0.01em',
          ...config.sx,
        }}
      />
    );
  };

  return (
    <Box sx={{ width: '100%', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Fixed Ultra-compact Unified Toolbar */}
      <Box
        sx={{
          flexShrink: 0,
          display: 'flex',
          flexWrap: 'wrap',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          p: 1,
          mb: 1.5,
          borderRadius: '8px',
          border: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
        }}
      >
        {/* Left Side: Selection, Search, and Status Chips */}
        <Stack
          direction="row"
          spacing={1}
          alignItems="center"
          sx={{ flexWrap: 'wrap', gap: 1 }}
        >
          {/* Select Button */}
          <MuiButton
            variant="outlined"
            size="small"
            onClick={(e: MouseEvent<HTMLButtonElement>) => setSelectMenuAnchor(e.currentTarget)}
            endIcon={<ChevronDown size={12} />}
            sx={{
              textTransform: 'none',
              fontWeight: 600,
              fontSize: '11px',
              color: 'text.primary',
              borderColor: 'grey.300',
              height: '30px',
              px: 1,
              borderRadius: '6px',
              '&:hover': { borderColor: 'grey.400', bgcolor: 'grey.50' },
            }}
          >
            <Checkbox
              checked={isAllCurrentSelected}
              indeterminate={selectedItems.length > 0 && !isAllCurrentSelected}
              size="small"
              sx={{ p: 0, mr: 0.5 }}
            />
            Select
          </MuiButton>

          <Menu
            anchorEl={selectMenuAnchor}
            open={Boolean(selectMenuAnchor)}
            onClose={() => setSelectMenuAnchor(null)}
            PaperProps={{ sx: { borderRadius: '6px', minWidth: 170, mt: 0.5 } }}
          >
            <MenuItem onClick={() => handleSelectAll('page')} sx={{ fontSize: '12px', py: 0.5 }}>
              Select Current Page ({currentItems.length})
            </MenuItem>
            <MenuItem onClick={() => handleSelectAll('all')} sx={{ fontSize: '12px', py: 0.5 }}>
              Select All Candidates ({filteredCandidates.length})
            </MenuItem>
            {selectedItems.length > 0 && (
              <MenuItem
                onClick={() => handleSelectAll('clear')}
                sx={{ fontSize: '12px', py: 0.5, color: 'error.main' }}
              >
                Clear Selection
              </MenuItem>
            )}
          </Menu>

          {/* Bulk Move (when selected) */}
          {selectedItems.length > 0 && (
            <>
              <MuiButton
                variant="contained"
                color="primary"
                size="small"
                startIcon={<Users size={12} />}
                endIcon={<ChevronDown size={12} />}
                onClick={(e: MouseEvent<HTMLButtonElement>) => setBulkMenuAnchor(e.currentTarget)}
                sx={{
                  textTransform: 'none',
                  fontWeight: 600,
                  fontSize: '11px',
                  height: '30px',
                  borderRadius: '6px',
                  boxShadow: 'none',
                  px: 1.2,
                }}
              >
                Bulk Move ({selectedItems.length})
              </MuiButton>

              <Menu
                anchorEl={bulkMenuAnchor}
                open={Boolean(bulkMenuAnchor)}
                onClose={() => setBulkMenuAnchor(null)}
                PaperProps={{ sx: { maxHeight: 280, width: 210, borderRadius: '6px', mt: 0.5 } }}
              >
                <Typography
                  variant="caption"
                  sx={{
                    px: 1.5,
                    py: 0.5,
                    display: 'block',
                    fontWeight: 700,
                    color: 'text.secondary',
                    textTransform: 'uppercase',
                    fontSize: '9.5px',
                  }}
                >
                  Move Selected To
                </Typography>
                <Divider sx={{ my: 0.5 }} />
                {options.map((option) => (
                  <MenuItem
                    key={option}
                    onClick={() => handleOptionClick(selectedItems, option)}
                    sx={{ fontSize: '12px', py: 0.5 }}
                  >
                    <Box
                      sx={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        bgcolor: 'primary.main',
                        mr: 1.2,
                      }}
                    />
                    Move to {option}
                  </MenuItem>
                ))}
              </Menu>
            </>
          )}

          {/* Compact Search Box */}
          <TextField
            placeholder="Search candidates..."
            size="small"
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
              setCurrentPage(1);
            }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start" sx={{ mr: 0.5 }}>
                  <Search size={13} color="#94a3b8" />
                </InputAdornment>
              ),
              endAdornment: searchTerm ? (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')} sx={{ p: 0.25 }}>
                    <X size={12} />
                  </IconButton>
                </InputAdornment>
              ) : null,
            }}
            sx={{
              width: { xs: 150, sm: 200, md: 220 },
              '& .MuiOutlinedInput-root': {
                borderRadius: '6px',
                height: '30px',
                fontSize: '11.5px',
                bgcolor: 'grey.50',
                '& fieldset': { borderColor: 'grey.300' },
              },
            }}
          />

          <Divider orientation="vertical" flexItem sx={{ height: 18, my: 'auto', mx: 0.25 }} />

          {/* Status Quick Filter Chips (Inline, right in the same line!) */}
          <Chip
            label={`All (${segmentedCandidates.length})`}
            size="small"
            clickable
            onClick={() => {
              setStatusFilter('ALL');
              setCurrentPage(1);
            }}
            color={statusFilter === 'ALL' ? 'primary' : 'default'}
            variant={statusFilter === 'ALL' ? 'filled' : 'outlined'}
            sx={{
              fontWeight: 600,
              fontSize: '11px',
              height: '24px',
              borderRadius: '5px',
            }}
          />

          {Object.entries(statusCounts).map(([status, count]) => {
            const isSelected = statusFilter === status;
            return (
              <Chip
                key={status}
                label={`${status} (${count})`}
                size="small"
                clickable
                onClick={() => {
                  setStatusFilter(status);
                  setCurrentPage(1);
                }}
                color={isSelected ? 'primary' : 'default'}
                variant={isSelected ? 'filled' : 'outlined'}
                sx={{
                  fontWeight: isSelected ? 600 : 500,
                  fontSize: '11px',
                  height: '24px',
                  borderRadius: '5px',
                  borderColor: isSelected ? 'primary.main' : 'grey.300',
                }}
              />
            );
          })}
        </Stack>

        {/* Right Side: View Mode, Page Size, Total Counter */}
        <Stack direction="row" spacing={1} alignItems="center" flexShrink={0}>
          <ToggleButtonGroup
            value={viewMode}
            exclusive
            onChange={(_, mode) => {
              if (mode) setViewMode(mode);
            }}
            size="small"
            sx={{
              height: '30px',
              '& .MuiToggleButton-root': {
                borderRadius: '6px',
                px: 1,
                py: 0.2,
                textTransform: 'none',
                borderColor: 'grey.300',
                '&.Mui-selected': {
                  bgcolor: 'primary.50',
                  color: 'primary.main',
                  fontWeight: 600,
                },
              },
            }}
          >
            <ToggleButton value="grid" aria-label="grid view">
              <LayoutGrid size={13} style={{ marginRight: 4 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '11px' }}>Grid</Typography>
            </ToggleButton>
            <ToggleButton value="table" aria-label="table view">
              <List size={13} style={{ marginRight: 4 }} />
              <Typography variant="caption" sx={{ fontWeight: 600, fontSize: '11px' }}>List</Typography>
            </ToggleButton>
          </ToggleButtonGroup>

          <FormControl size="small">
            <Select
              value={pageSize}
              onChange={(e: SelectChangeEvent<number>) => {
                setPageSize(Number(e.target.value));
                setCurrentPage(1);
              }}
              sx={{
                height: '30px',
                fontSize: '11px',
                fontWeight: 500,
                borderRadius: '6px',
                bgcolor: 'background.paper',
                '& .MuiOutlinedInput-notchedOutline': { borderColor: 'grey.300' },
              }}
            >
              <MenuItem value={8} sx={{ fontSize: '11px' }}>8 / page</MenuItem>
              <MenuItem value={12} sx={{ fontSize: '11px' }}>12 / page</MenuItem>
              <MenuItem value={16} sx={{ fontSize: '11px' }}>16 / page</MenuItem>
              <MenuItem value={24} sx={{ fontSize: '11px' }}>24 / page</MenuItem>
              <MenuItem value={0} sx={{ fontSize: '11px' }}>All ({filteredCandidates.length})</MenuItem>
            </Select>
          </FormControl>

          <Chip
            label={`${filteredCandidates.length}${
              filteredCandidates.length !== segmentedCandidates.length
                ? ` / ${segmentedCandidates.length}`
                : ''
            } candidates`}
            size="small"
            sx={{
              fontWeight: 600,
              fontSize: '11px',
              height: '30px',
              borderRadius: '6px',
              bgcolor: 'grey.100',
              color: 'text.primary',
            }}
          />
        </Stack>
      </Box>

      {/* Scrollable Mid-Content Area */}
      <Box
        sx={{
          flex: 1,
          minHeight: 0,
          overflowY: 'auto',
          pr: 0.5,
          '&::-webkit-scrollbar': {
            width: '6px',
          },
          '&::-webkit-scrollbar-track': {
            background: 'transparent',
          },
          '&::-webkit-scrollbar-thumb': {
            background: '#cbd5e1',
            borderRadius: '4px',
          },
          '&::-webkit-scrollbar-thumb:hover': {
            background: '#94a3b8',
          },
        }}
      >
        {currentItems.length === 0 ? (
          <Paper
            elevation={0}
            sx={{
              p: 6,
              textAlign: 'center',
              border: '1px dashed',
              borderColor: 'grey.300',
              borderRadius: '12px',
            }}
          >
            <Filter size={36} color="#cbd5e1" style={{ margin: '0 auto 12px auto' }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
              No candidates found
            </Typography>
            <Typography variant="body2" sx={{ color: 'text.secondary', mt: 0.5, mb: 2 }}>
              Try adjusting your search query or status filter.
            </Typography>
            {(searchTerm || statusFilter !== 'ALL') && (
              <MuiButton
                variant="outlined"
                size="small"
                onClick={() => {
                  setSearchTerm('');
                  setStatusFilter('ALL');
                }}
                sx={{ textTransform: 'none', borderRadius: '8px' }}
              >
                Reset Filters
              </MuiButton>
            )}
          </Paper>
        ) : viewMode === 'grid' ? (
          /* Responsive 3-Column Grid on Desktop for perfectly proportioned cards */
          <Grid container spacing={2.5}>
            {currentItems.map((item) => {
              const isSelected = selectedItems.includes(item.userId);
              const skills = item.matchedSkills || [];
              const displayedSkills =
                filteredSkills && filteredSkills.length > 0
                  ? skills.filter((skill) =>
                      filteredSkills.some(
                        (fs) => fs.toLowerCase() === skill.toLowerCase()
                      )
                    )
                  : skills;

              const experience = Number(item.value?.[1] ?? item.experience ?? 0).toFixed(1);
              const skillsCount = useSkillPercentage
                ? `${Number(item.value?.[0] ?? 0).toFixed(1)}%`
                : `${Number(item.value?.[0] ?? item.noOfSkills ?? 0)}`;

              return (
                <Grid item xs={12} sm={6} lg={4} key={item.userId}>
                  <Card
                    elevation={0}
                    sx={{
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                      justifyContent: 'space-between',
                      borderRadius: '12px',
                      border: '1px solid',
                      borderColor: isSelected ? 'primary.main' : 'divider',
                      bgcolor: isSelected ? 'rgba(37, 99, 235, 0.04)' : 'background.paper',
                      boxShadow: isSelected
                        ? '0 0 0 1px #2563eb, 0 4px 12px rgba(37, 99, 235, 0.08)'
                        : '0 1px 3px rgba(0,0,0,0.04)',
                      transition: 'all 0.2s ease-in-out',
                      '&:hover': {
                        borderColor: isSelected ? 'primary.main' : 'primary.light',
                        boxShadow: '0 4px 16px rgba(0,0,0,0.08)',
                      },
                    }}
                  >
                    <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
                      {/* Top: Checkbox, Avatar, Name & Status */}
                      <Box
                        sx={{
                          display: 'flex',
                          alignItems: 'flex-start',
                          justifyContent: 'space-between',
                          gap: 1.5,
                        }}
                      >
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleSelectItem(item.userId)}
                            size="small"
                            sx={{ p: 0.25 }}
                          />
                          <Box
                            onClick={() =>
                              openTab(
                                item.name || `${item.firstName} ${item.lastName}`,
                                item.userId
                              )
                            }
                            sx={{ cursor: 'pointer', flexShrink: 0 }}
                          >
                            {renderAvatar(item)}
                          </Box>
                          <Box sx={{ minWidth: 0, flex: 1 }}>
                            <Typography
                              variant="subtitle2"
                              onClick={() =>
                                openTab(
                                  item.name || `${item.firstName} ${item.lastName}`,
                                  item.userId
                                )
                              }
                              sx={{
                                fontWeight: 700,
                                fontSize: '13.5px',
                                color: 'text.primary',
                                cursor: 'pointer',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                '&:hover': { color: 'primary.main' },
                              }}
                              title={item.name || `${item.firstName} ${item.lastName}`}
                            >
                              {item.name || `${item.firstName} ${item.lastName}`}
                            </Typography>
                            <Typography
                              variant="caption"
                              sx={{
                                color: 'text.secondary',
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                fontSize: '11px',
                              }}
                            >
                              {item.email || 'Applicant'}
                            </Typography>
                          </Box>
                        </Stack>

                        {/* Status Chip */}
                        <Box sx={{ flexShrink: 0 }}>
                          {renderStatusChip(item.applicantStatus)}
                        </Box>
                      </Box>

                      {/* Key Metrics Row */}
                      <Stack direction="row" spacing={1} sx={{ mt: 1.5, alignItems: 'center' }}>
                        <Chip
                          icon={<Briefcase size={12} style={{ marginLeft: 4 }} />}
                          label={`${experience} yrs exp`}
                          size="small"
                          sx={{
                            fontSize: '11px',
                            fontWeight: 500,
                            height: '24px',
                            bgcolor: 'grey.100',
                            color: 'text.primary',
                            borderRadius: '6px',
                            border: '1px solid',
                            borderColor: 'grey.200',
                          }}
                        />
                        <Chip
                          icon={<Sparkles size={12} style={{ marginLeft: 4, color: '#2563eb' }} />}
                          label={`${skillsCount} ${useSkillPercentage ? 'Match' : 'Skills'}`}
                          size="small"
                          sx={{
                            fontSize: '11px',
                            fontWeight: 600,
                            height: '24px',
                            bgcolor: 'rgba(37, 99, 235, 0.08)',
                            color: 'primary.dark',
                            borderRadius: '6px',
                            border: '1px solid rgba(37, 99, 235, 0.15)',
                          }}
                        />
                      </Stack>

                      {/* Matched Skills Container */}
                      <Box sx={{ mt: 1.5 }}>
                        <Typography
                          variant="caption"
                          sx={{
                            fontWeight: 700,
                            fontSize: '10px',
                            textTransform: 'uppercase',
                            letterSpacing: '0.04em',
                            color: 'text.secondary',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Matched Skills
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, minHeight: '26px' }}>
                          {displayedSkills.length > 0 ? (
                            <>
                              {displayedSkills.slice(0, 3).map((skill, idx) => (
                                <Chip
                                  key={idx}
                                  label={skill}
                                  size="small"
                                  sx={{
                                    fontSize: '11px',
                                    height: '22px',
                                    maxWidth: '180px',
                                    bgcolor: 'grey.50',
                                    color: 'text.primary',
                                    border: '1px solid',
                                    borderColor: 'grey.200',
                                    borderRadius: '6px',
                                  }}
                                  title={skill}
                                />
                              ))}
                              {displayedSkills.length > 3 && (
                                <Tooltip title={displayedSkills.slice(3).join(', ')}>
                                  <Chip
                                    label={`+${displayedSkills.length - 3} more`}
                                    size="small"
                                    sx={{
                                      fontSize: '10px',
                                      height: '22px',
                                      fontWeight: 600,
                                      bgcolor: 'grey.200',
                                      color: 'text.secondary',
                                      borderRadius: '6px',
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </>
                          ) : (
                            <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic', fontSize: '11px' }}>
                              No matched skills recorded
                            </Typography>
                          )}
                        </Box>
                      </Box>

                      {/* Behavioral & Keywords Row */}
                      {(item.matchedBehavior?.length || item.keywords?.length) ? (
                        <Box
                          sx={{
                            mt: 1.5,
                            pt: 1,
                            borderTop: '1px solid',
                            borderColor: 'grey.100',
                          }}
                        >
                          {item.matchedBehavior && item.matchedBehavior.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: 'text.secondary',
                                fontSize: '11px',
                              }}
                            >
                              <strong>Behavioral:</strong> {item.matchedBehavior.join(', ')}
                            </Typography>
                          )}
                          {item.keywords && item.keywords.length > 0 && (
                            <Typography
                              variant="caption"
                              sx={{
                                display: 'block',
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                                color: 'text.secondary',
                                fontSize: '11px',
                              }}
                            >
                              <strong>Keywords:</strong> {item.keywords.join(', ')}
                            </Typography>
                          )}
                        </Box>
                      ) : null}
                    </CardContent>

                    {/* Footer Actions: Cleanly pinned to bottom */}
                    <CardActions
                      sx={{
                        px: 2,
                        py: 1.25,
                        borderTop: '1px solid',
                        borderColor: 'grey.100',
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        bgcolor: 'background.paper',
                      }}
                    >
                      <MuiButton
                        size="small"
                        variant="text"
                        endIcon={<ExternalLink size={13} />}
                        onClick={() =>
                          openTab(
                            item.name || `${item.firstName} ${item.lastName}`,
                            item.userId
                          )
                        }
                        sx={{
                          fontSize: '12px',
                          fontWeight: 600,
                          textTransform: 'none',
                          p: 0,
                          minWidth: 0,
                          '&:hover': { bgcolor: 'transparent', textDecoration: 'underline' },
                        }}
                      >
                        View Profile
                      </MuiButton>

                      <MoveDropdown
                        options={options}
                        onSelect={(option) => handleOptionClick(item.userId, option)}
                        isOpen={openDropdown === item.userId}
                        onToggle={() => handleDropdownToggle(item.userId)}
                      />
                    </CardActions>
                  </Card>
                </Grid>
              );
            })}
          </Grid>
        ) : (
          /* Dense Table / List View */
          <TableContainer
            component={Paper}
            elevation={0}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: '6px',
              overflow: 'hidden',
            }}
          >
            <Table size="small">
              <TableHead>
                <TableRow
                  sx={{
                    bgcolor: 'grey.50',
                    '& th': {
                      fontWeight: 700,
                      fontSize: '11px',
                      textTransform: 'uppercase',
                      color: 'text.secondary',
                      py: 1.5,
                    },
                  }}
                >
                  <TableCell padding="checkbox" align="center">
                    <Checkbox
                      checked={isAllCurrentSelected}
                      indeterminate={selectedItems.length > 0 && !isAllCurrentSelected}
                      onChange={() => handleSelectAll('page')}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>Candidate</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell>Skills Match</TableCell>
                  <TableCell>Matched Skills</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {currentItems.map((item) => {
                  const isSelected = selectedItems.includes(item.userId);
                  const skills = item.matchedSkills || [];
                  const experience = Number(item.value?.[1] ?? item.experience ?? 0).toFixed(1);
                  const skillsCount = useSkillPercentage
                    ? `${Number(item.value?.[0] ?? 0).toFixed(1)}%`
                    : `${Number(item.value?.[0] ?? item.noOfSkills ?? 0)}`;

                  return (
                    <TableRow
                      key={item.userId}
                      hover
                      sx={{
                        bgcolor: isSelected ? 'rgba(37, 99, 235, 0.04)' : 'inherit',
                      }}
                    >
                      <TableCell padding="checkbox" align="center">
                        <Checkbox
                          checked={isSelected}
                          onChange={() => toggleSelectItem(item.userId)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1.5} alignItems="center">
                          <Box
                            onClick={() =>
                              openTab(
                                item.name || `${item.firstName} ${item.lastName}`,
                                item.userId
                              )
                            }
                            sx={{ cursor: 'pointer' }}
                          >
                            {renderAvatar(item)}
                          </Box>
                          <Box sx={{ minWidth: 0 }}>
                            <Typography
                              variant="subtitle2"
                              onClick={() =>
                                openTab(
                                  item.name || `${item.firstName} ${item.lastName}`,
                                  item.userId
                                )
                              }
                              sx={{
                                fontWeight: 600,
                                fontSize: '13px',
                                cursor: 'pointer',
                                color: 'text.primary',
                                '&:hover': { color: 'primary.main' },
                              }}
                            >
                              {item.name || `${item.firstName} ${item.lastName}`}
                            </Typography>
                            <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '11px' }}>
                              {item.email || 'Applicant'}
                            </Typography>
                          </Box>
                        </Stack>
                      </TableCell>
                      <TableCell>{renderStatusChip(item.applicantStatus)}</TableCell>
                      <TableCell sx={{ fontSize: '12px', fontWeight: 500 }}>
                        {experience} yrs
                      </TableCell>
                      <TableCell sx={{ fontSize: '12px', fontWeight: 600, color: 'primary.main' }}>
                        {skillsCount}
                      </TableCell>
                      <TableCell sx={{ maxWidth: 300 }}>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                          {skills.slice(0, 2).map((s, idx) => (
                            <Chip
                              key={idx}
                              label={s}
                              size="small"
                              sx={{
                                fontSize: '10.5px',
                                height: '20px',
                                maxWidth: '140px',
                                bgcolor: 'grey.100',
                              }}
                              title={s}
                            />
                          ))}
                          {skills.length > 2 && (
                            <Tooltip title={skills.slice(2).join(', ')}>
                              <Chip
                                label={`+${skills.length - 2}`}
                                size="small"
                                sx={{ fontSize: '10px', height: '20px', fontWeight: 600 }}
                              />
                            </Tooltip>
                          )}
                        </Box>
                      </TableCell>
                      <TableCell align="right">
                        <Stack direction="row" spacing={1} justifyContent="flex-end" alignItems="center">
                          <IconButton
                            size="small"
                            onClick={() =>
                              openTab(
                                item.name || `${item.firstName} ${item.lastName}`,
                                item.userId
                              )
                            }
                            sx={{
                              border: '1px solid',
                              borderColor: 'grey.300',
                              borderRadius: '6px',
                              p: 0.5,
                            }}
                            title="View Profile"
                          >
                            <ExternalLink size={14} />
                          </IconButton>
                          <MoveDropdown
                            options={options}
                            onSelect={(option) => handleOptionClick(item.userId, option)}
                            isOpen={openDropdown === item.userId}
                            onToggle={() => handleDropdownToggle(item.userId)}
                          />
                        </Stack>
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Box>

      {/* Fixed Pagination Footer */}
      <Box
        sx={{
          flexShrink: 0,
          mt: 1.5,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 1.5,
        }}
      >
        <Typography variant="caption" sx={{ color: 'text.secondary', fontSize: '12px' }}>
          Showing <strong>{filteredCandidates.length > 0 ? indexOfFirstItem + 1 : 0}</strong> to{' '}
          <strong>{Math.min(indexOfLastItem, filteredCandidates.length)}</strong> of{' '}
          <strong>{filteredCandidates.length}</strong> candidates
        </Typography>

        {totalPages > 1 && (
          <Pagination
            count={totalPages}
            page={currentPage}
            onChange={(_, page) => setCurrentPage(page)}
            color="primary"
            size="small"
            shape="rounded"
            showFirstButton
            showLastButton
            sx={{
              '& .MuiPaginationItem-root': {
                fontWeight: 600,
                fontSize: '12px',
                borderRadius: '6px',
              },
            }}
          />
        )}
      </Box>
    </Box>
  );
};
